import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsSpeaking, useLocalParticipant, useParticipantAttributes, useParticipants } from '@livekit/components-react';
import { type Participant, RoomEvent } from 'livekit-client';

import { i18n } from '../../i18n';
import { notifyError } from '../../shared/notify';

export const HAND_ATTRIBUTE = 'hand_raised_at';
const SPEECH_LOWERS_HAND_AFTER_MS = 1000;

export function handRaisedAt(attributes: Readonly<Record<string, string>> | undefined): number {
    const raisedAt = Number(attributes?.[HAND_ATTRIBUTE]);
    return Number.isFinite(raisedAt) ? raisedAt : 0;
}

export function useLocalHand() {
    const { localParticipant } = useLocalParticipant();
    const { attributes } = useParticipantAttributes({ participant: localParticipant });
    const raised = handRaisedAt(attributes) > 0;
    const [pending, setPending] = useState(false);
    const inFlightRef = useRef(false);

    const toggle = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setPending(true);
        try {
            await localParticipant.setAttributes({ [HAND_ATTRIBUTE]: raised ? '' : String(Date.now()) });
        } catch (error) {
            notifyError(i18n.t(raised ? 'reactions:hand.lowerError' : 'reactions:hand.raiseError'), error);
        } finally {
            inFlightRef.current = false;
            setPending(false);
        }
    }, [localParticipant, raised]);

    return { raised, pending, toggle };
}

export function LowerHandOnSpeech() {
    const { localParticipant } = useLocalParticipant();
    const { attributes } = useParticipantAttributes({ participant: localParticipant });
    const raised = handRaisedAt(attributes) > 0;
    const speaking = useIsSpeaking(localParticipant);

    useEffect(() => {
        if (!raised || !speaking) return;
        const timeout = setTimeout(() => {
            localParticipant.setAttributes({ [HAND_ATTRIBUTE]: '' }).catch((error: unknown) => {
                notifyError(i18n.t('reactions:hand.lowerError'), error);
            });
        }, SPEECH_LOWERS_HAND_AFTER_MS);
        return () => {
            clearTimeout(timeout);
        };
    }, [localParticipant, raised, speaking]);

    return null;
}

export function useRaisedHands(): Participant[] {
    const participants = useParticipants({ updateOnlyOn: [RoomEvent.ParticipantAttributesChanged] });
    return useMemo(
        () =>
            participants
                .map((participant) => ({ participant, raisedAt: handRaisedAt(participant.attributes) }))
                .filter(({ raisedAt }) => raisedAt > 0)
                .toSorted((a, b) => a.raisedAt - b.raisedAt)
                .map(({ participant }) => participant),
        [participants]
    );
}

export function useIsHandRaised(participant: Participant): boolean {
    const { attributes } = useParticipantAttributes({ participant });
    return handRaisedAt(attributes) > 0;
}
